import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { DEFAULT_THEME_COLORS } from "@wealthx/ui/theme";

type TenantThemeColors = {
  primary?: string;
  secondary?: string;
};

type TenantThemeContextValue = {
  colors: TenantThemeColors;
  setColors: (colors: TenantThemeColors) => void;
};

const isValidHexColor = (color: string): boolean =>
  /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color);

const TenantThemeContext = createContext<TenantThemeContextValue>({
  colors: {},
  setColors: () => {},
});

export const useTenantTheme = () => useContext(TenantThemeContext);

export const TenantThemeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [colors, setColorsState] = useState<TenantThemeColors>({});

  const setColors = useCallback((next: TenantThemeColors) => {
    setColorsState((prev) => {
      const sanitized: TenantThemeColors = {
        primary:
          next.primary && isValidHexColor(next.primary)
            ? next.primary
            : DEFAULT_THEME_COLORS.primary,
        secondary:
          next.secondary && isValidHexColor(next.secondary)
            ? next.secondary
            : DEFAULT_THEME_COLORS.secondary,
      };
      if (
        prev.primary === sanitized.primary &&
        prev.secondary === sanitized.secondary
      ) {
        return prev;
      }
      return sanitized;
    });
  }, []);

  const value = useMemo(() => ({ colors, setColors }), [colors, setColors]);

  return (
    <TenantThemeContext.Provider value={value}>
      {children}
    </TenantThemeContext.Provider>
  );
};
